import {
  Box, Paper, Tab, Tabs
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { PageLayout } from '../components/ui/PageLayout';
import { useSnackbar } from '../contexts/SnackbarContext';
import i18n from '../i18n/i18n';

import { UserRole } from '../api/data-contracts';
import { log } from '../utils/errors/logger';
import AuthService from '../services/AuthService';
import { AccountSettingsTab } from '../components/settings/AccountSettingsTab';
import { MyDataTab } from '../components/settings/MyDataTab';
import { useUpdateUser, useChangePassword, useRequestDeletion, useCancelDeletion } from '../hooks/api/useUsers';
import { type AppUser } from '../types/common';

const Settings: React.FC = () => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useSnackbar();
  const updateUser = useUpdateUser();
  const changePassword = useChangePassword();
  const requestDeletion = useRequestDeletion();
  const cancelDeletion = useCancelDeletion();
  const [tabValue, setTabValue] = useState(0);
  const [user, setUser] = useState<AppUser | null>(AuthService.getUser());
  const [userForm, setUserForm] = useState({
    email: '',
    full_name: '',
    company: '',
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  // Language preference state
  const [currentLanguage, setCurrentLanguage] = useState<string>(i18n.language || 'de');

  // Load user data on component mount
  useEffect(() => {
    // Initialize form with current user data
    const currentUser = AuthService.getUser();
    if (currentUser) {
      setUserForm(prev => ({
        ...prev,
        email: currentUser.email || '',
        full_name: currentUser.full_name || '',
        company: currentUser.company || ''
      }));
    }
  }, []);

  // Identity-link callback handling: the backend redirects users back to
  // /settings?identity_link=<provider>&status=success|error after they
  // complete the OAuth flow with the external IdP. Surface a toast,
  // refresh the linked-accounts query, and clean the URL so a refresh
  // doesn't re-trigger the toast.
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const linkProvider = searchParams.get('identity_link');
    const linkStatus = searchParams.get('status');
    if (!linkProvider || !linkStatus) { return; }

    if (linkStatus === 'success') {
      showSuccess(t('settings.linkedAccounts.success'));
    } else {
      showError(t('settings.linkedAccounts.error'));
    }
    queryClient.invalidateQueries({ queryKey: ['identity-link', 'status'] });
    // Drop the query params without leaving a history entry.
    const next = new URLSearchParams(searchParams);
    next.delete('identity_link');
    next.delete('status');
    setSearchParams(next, { replace: true });
    // Switch to the Account tab so the user immediately sees the result.
    setTabValue(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle language change
  const handleLanguageChange = useCallback(async (language: string) => {
    try {
      // i18n.changeLanguage automatically saves to cookie
      await i18n.changeLanguage(language);
      setCurrentLanguage(language);
      log.info('Language changed successfully', { language });
    } catch (error) {
      log.error('Error changing language', { error });
    }
  }, [i18n]);

  // Stable, void-returning wrapper passed to LanguageSelector so its memo holds
  // (a new inline arrow each render would defeat it) and the void-return prop
  // type is satisfied.
  const handleLanguageChangeSync = useCallback((language: string) => {
    void handleLanguageChange(language);
  }, [handleLanguageChange]);

  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Handle form change
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setUserForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Submit profile update
  const handleProfileUpdate = async () => {
    if (updateUser.isPending) {return;}

    try {
      // Update profile via API
      if (!user?.username) {
        throw new Error(t('settings.messages.userUsernameNotFound'));
      }

      const updatedUser = await updateUser.mutateAsync({
        username: user.username,
        data: { full_name: userForm.full_name }
      });

      // Preserve permissions from current user (backend returns User model without permissions)
      const currentUser = AuthService.getUser();
      const userWithPermissions: AppUser = {
        ...updatedUser,
        user_id: updatedUser.id,
        id: updatedUser.id,
        role: currentUser?.role ?? UserRole.Standard,
        permissions: currentUser?.permissions ?? ({} as AppUser['permissions']),
      };

      // Update local user data with response from backend
      AuthService.setUser({
        ...userWithPermissions,
        role:
          userWithPermissions.role === 'superadmin'
            ? UserRole.Admin
            : userWithPermissions.role,
      });
      setUser(userWithPermissions);

      showSuccess(t('settings.messages.profileUpdatedSuccessfully'));
    } catch (error) {
      log.error('Error updating profile', { error });
      showError(t('settings.messages.failedToUpdateProfile'));
    }
  };

  // Submit password change
  const handlePasswordChange = async () => {
    if (changePassword.isPending) {return;}

    // Validate passwords
    if (userForm.new_password !== userForm.confirm_password) {
      showError(t('settings.messages.passwordsDoNotMatch'));
      return;
    }

    if (userForm.new_password.length < 8) {
      showError(t('settings.messages.passwordMinLength'));
      return;
    }

    if (!userForm.current_password) {
      showError(t('settings.messages.currentPasswordRequired'));
      return;
    }

    try {
      // Call new secure endpoint with current_password validation
      await changePassword.mutateAsync({
        current_password: userForm.current_password,
        new_password: userForm.new_password
      });

      showSuccess(t('settings.messages.passwordChangedSuccessfully'));

      // Reset password fields
      setUserForm(prev => ({
        ...prev,
        current_password: '',
        new_password: '',
        confirm_password: ''
      }));
    } catch (error: unknown) {
      log.error('Error changing password', { error });

      // Check for specific error about incorrect current password
      if ((error as { response?: { status?: number } })?.response?.status === 401) {
        showError(t('settings.messages.incorrectCurrentPassword'));
      } else {
        showError(t('settings.messages.failedToChangePassword'));
      }
    }
  };

  // Handle account deletion request
  const handleRequestDeletion = async () => {
    if (requestDeletion.isPending) {return;}

    try {
      await requestDeletion.mutateAsync();
      showSuccess(t('users.deletionRequested'));

      // Refresh user data to reflect deletion status
      const updatedUser = AuthService.getUser();
      setUser(updatedUser);
    } catch (error: unknown) {
      log.error('Error requesting deletion', { error });
      showError(t('user.error'));
    }
  };

  // Handle cancellation of account deletion
  const handleCancelDeletion = async () => {
    if (cancelDeletion.isPending) {return;}

    try {
      if (!user?.username) {
        throw new Error('User username not found');
      }

      await cancelDeletion.mutateAsync(user.username);
      showSuccess(t('users.deletionCancelled'));

      // Refresh user data to reflect cancellation
      const updatedUser = AuthService.getUser();
      setUser(updatedUser);
    } catch (error: unknown) {
      log.error('Error canceling deletion', { error });
      showError(t('user.error'));
    }
  };

  return (
    <>
      <PageLayout
        title={t('settings.title')}
        isLoading={false}
        maxWidth="lg"
      >
        {/* Settings Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label={t('settings.tabs.account')} />
            <Tab label={t('settings.myData.title')} />
          </Tabs>
        </Paper>

        {/* Tab Content */}
        <Box>
          {tabValue === 0 && (
            <AccountSettingsTab
              userForm={userForm}
              loading={updateUser.isPending || changePassword.isPending}
              currentLanguage={currentLanguage}
              user={user}
              onFormChange={handleFormChange}
              onProfileUpdate={() => { void handleProfileUpdate(); }}
              onPasswordChange={() => { void handlePasswordChange(); }}
              onLanguageChange={handleLanguageChangeSync}
              onRequestDeletion={() => { void handleRequestDeletion(); }}
              onCancelDeletion={() => { void handleCancelDeletion(); }}
              deletionLoading={requestDeletion.isPending || cancelDeletion.isPending}
            />
          )}
          {tabValue === 1 && <MyDataTab />}
        </Box>
      </PageLayout>
    </>
  );
};

export default Settings;
